import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  Timer,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../../hooks/useAuth";
import { useMyWorkAdjustments, useSubmitWorkAdjustment } from "../../../hooks/useWorkAdjustment";
import { Modal } from "../../../components/ui/Modal";
import type { WorkAdjustmentRequest, WorkAdjustmentType } from "../../../lib/workAdjustmentApi";

const submitSchema = z
  .object({
    type: z.enum(["OVERTIME", "HOLIDAY_WORK", "HOLIDAY_OVERTIME"]),
    workDate: z.string().min(1, "Work date is required"),
    overtimeHours: z.preprocess(
      (v) => (typeof v === "number" && isNaN(v) ? undefined : v),
      z.number().min(0.5, "Minimum 0.5 hours").max(12, "Maximum 12 hours").optional(),
    ),
    description: z.string().min(1, "Description is required").max(500),
  })
  .refine(
    (d) => {
      if (d.type === "OVERTIME" || d.type === "HOLIDAY_OVERTIME") {
        return d.overtimeHours !== undefined && d.overtimeHours > 0;
      }
      return true;
    },
    { message: "Overtime hours are required for this type", path: ["overtimeHours"] },
  );

type SubmitForm = z.infer<typeof submitSchema>;

const STATUS_CONFIG: Record<
  WorkAdjustmentRequest["status"],
  { label: string; badgeColor: string; borderColor: string; icon: typeof Clock }
> = {
  PENDING:  { label: "Pending",  badgeColor: "bg-[#fef9c3] text-[#a16207]", borderColor: "border-l-[#f59e0b]", icon: Clock        },
  APPROVED: { label: "Approved", badgeColor: "bg-[#dcfce7] text-[#15803d]", borderColor: "border-l-[#16a34a]", icon: CheckCircle2 },
  REJECTED: { label: "Rejected", badgeColor: "bg-[#fee2e2] text-[#b91c1c]", borderColor: "border-l-[#dc2626]", icon: XCircle      },
};

const TYPE_LABEL: Record<WorkAdjustmentType, string> = {
  OVERTIME:         "Overtime",
  HOLIDAY_WORK:     "Holiday Work",
  HOLIDAY_OVERTIME: "Holiday + Overtime",
};

const HOLIDAY_TYPE_LABEL: Record<string, string> = {
  REGULAR_HOLIDAY:    "Regular Holiday (200%)",
  SPECIAL_NON_WORKING: "Special Non-Working (130%)",
};

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";

function WorkAdjCard({ item }: { item: WorkAdjustmentRequest }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[item.status];
  const Icon = cfg.icon;

  return (
    <div className={`bg-white border border-[#e2e8f0] border-l-4 ${cfg.borderColor} rounded-xl overflow-hidden shadow-sm`}>
      <button
        type="button"
        className="w-full text-left px-5 py-4 flex items-start justify-between gap-4 hover:bg-[#f8fafc] transition-colors"
        onClick={() => setExpanded((x) => !x)}
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${cfg.badgeColor}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-[#0f172a] text-base">{TYPE_LABEL[item.type]}</p>
            <p className="text-xs text-[#94a3b8] mt-0.5">
              {format(new Date(item.workDate), "MMM d, yyyy")} · Period {item.period}
              {item.overtimeHours ? ` · ${item.overtimeHours}h OT` : ""}
            </p>
            {item.status === "APPROVED" && item.payslipId && (
              <p className="text-xs text-[#16a34a] mt-0.5">Included in payslip</p>
            )}
            {item.status === "APPROVED" && !item.payslipId && (
              <p className="text-xs text-[#2447d9] mt-0.5">Pending next payroll</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.badgeColor}`}>
            {cfg.label}
          </span>
          {expanded
            ? <ChevronUp className="h-4 w-4 text-[#94a3b8]" />
            : <ChevronDown className="h-4 w-4 text-[#94a3b8]" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#f1f5f9] px-5 py-4 bg-[#f8fafc] space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-[#94a3b8] shrink-0">Description</span>
            <span className="text-[#374151] text-right">{item.description}</span>
          </div>
          {item.overtimeHours && (
            <div className="flex justify-between">
              <span className="text-[#94a3b8]">OT Hours</span>
              <span className="text-[#374151]">{item.overtimeHours}h</span>
            </div>
          )}
          {item.holidayType && (
            <div className="flex justify-between">
              <span className="text-[#94a3b8]">Holiday Type</span>
              <span className="text-[#374151]">{HOLIDAY_TYPE_LABEL[item.holidayType] ?? item.holidayType}</span>
            </div>
          )}
          {item.reviewedAt && (
            <div className="flex justify-between">
              <span className="text-[#94a3b8]">Reviewed on</span>
              <span className="text-[#374151]">{format(new Date(item.reviewedAt), "MMM d, yyyy")}</span>
            </div>
          )}
          {item.rejectionReason && (
            <div className="flex justify-between gap-4">
              <span className="text-[#94a3b8] shrink-0">Rejection reason</span>
              <span className="text-[#dc2626] text-right">{item.rejectionReason}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-[#94a3b8]">Submitted on</span>
            <span className="text-[#374151]">{format(new Date(item.createdAt), "MMM d, yyyy")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkAdjustmentsPage() {
  const { activeBusinessId } = useAuth();
  const businessId = activeBusinessId ?? "";
  const [showModal, setShowModal] = useState(false);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMyWorkAdjustments(businessId, !!businessId);

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const submit = useSubmitWorkAdjustment(businessId);

  const form = useForm<SubmitForm>({
    resolver: zodResolver(submitSchema),
    defaultValues: {
      type: "OVERTIME",
      description: "",
      workDate: new Date().toISOString().split("T")[0],
    },
  });

  const watchType = form.watch("type");

  function handleClose() {
    setShowModal(false);
    form.reset({
      type: "OVERTIME",
      description: "",
      workDate: new Date().toISOString().split("T")[0],
    });
    submit.reset();
  }

  async function onSubmit(values: SubmitForm) {
    await submit.mutateAsync(values);
    handleClose();
  }

  const pendingCount  = items.filter((i) => i.status === "PENDING").length;
  const approvedCount = items.filter((i) => i.status === "APPROVED").length;
  const rejectedCount = items.filter((i) => i.status === "REJECTED").length;

  const activeItems  = items.filter((i) => i.status === "PENDING" || i.status === "APPROVED");
  const historyItems = items.filter((i) => i.status === "REJECTED");

  return (
    <div className="space-y-5">

      {/* ── Summary bar ───────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-[#c7d2fe] bg-gradient-to-br from-[#eff3ff] to-[#e0e7ff] p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2447d9]">
              <Timer className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#0f172a]">Work Time Claims</h1>
              <p className="text-xs text-[#64748b]">Submit overtime and holiday pay requests</p>
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors shrink-0"
          >
            <Plus className="h-4 w-4" />
            New Request
          </button>
        </div>

        {!isLoading && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="rounded-xl bg-white/60 px-3 py-2.5">
              <p className="text-[10px] font-semibold text-[#6366f1] uppercase tracking-wide mb-0.5">Pending</p>
              <p className={`text-sm font-bold ${pendingCount > 0 ? "text-[#a16207]" : "text-[#94a3b8]"}`}>
                {pendingCount}
              </p>
              <p className="text-[10px] text-[#94a3b8] mt-0.5">Awaiting review</p>
            </div>
            <div className="rounded-xl bg-white/60 px-3 py-2.5">
              <p className="text-[10px] font-semibold text-[#6366f1] uppercase tracking-wide mb-0.5">Approved</p>
              <p className={`text-sm font-bold ${approvedCount > 0 ? "text-[#15803d]" : "text-[#94a3b8]"}`}>
                {approvedCount}
              </p>
              <p className="text-[10px] text-[#94a3b8] mt-0.5">For next payroll</p>
            </div>
            <div className="rounded-xl bg-white/60 px-3 py-2.5">
              <p className="text-[10px] font-semibold text-[#6366f1] uppercase tracking-wide mb-0.5">Rejected</p>
              <p className={`text-sm font-bold ${rejectedCount > 0 ? "text-[#b91c1c]" : "text-[#94a3b8]"}`}>
                {rejectedCount}
              </p>
              <p className="text-[10px] text-[#94a3b8] mt-0.5">Not approved</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-[#2447d9]" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-5 py-4 text-sm text-[#dc2626]">
          Failed to load work time claims. Please try again.
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#eff3ff]">
            <Timer className="h-7 w-7 text-[#2447d9]" />
          </div>
          <p className="font-medium text-[#0f172a]">No work time claims yet</p>
          <p className="text-sm text-[#94a3b8]">Tap "New Request" above to submit your first claim.</p>
        </div>
      ) : (
        <>
          {activeItems.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">Active</p>
              {activeItems.map((item) => <WorkAdjCard key={item.requestId} item={item} />)}
            </section>
          )}
          {historyItems.length > 0 && (
            <section className="space-y-3">
              <p className="text-xs font-semibold text-[#94a3b8] uppercase tracking-widest px-0.5">History</p>
              {historyItems.map((item) => <WorkAdjCard key={item.requestId} item={item} />)}
            </section>
          )}
          {hasNextPage && (
            <div className="flex justify-center pt-1">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-lg border border-[#e2e8f0] bg-white px-5 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] disabled:opacity-50 transition-colors"
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {/* ── Submit Modal ───────────────────────────────────────────────────── */}
      <Modal isOpen={showModal} title="New Work Time Claim" onClose={handleClose}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className={LABEL_CLASS}>Type</label>
            <select {...form.register("type")} className={INPUT_CLASS}>
              <option value="OVERTIME">Overtime</option>
              <option value="HOLIDAY_WORK">Holiday Work (no OT)</option>
              <option value="HOLIDAY_OVERTIME">Holiday + Overtime</option>
            </select>
            {form.formState.errors.type && (
              <p className="mt-1 text-xs text-[#dc2626]">{form.formState.errors.type.message}</p>
            )}
          </div>

          <div>
            <label className={LABEL_CLASS}>Work Date</label>
            <input type="date" {...form.register("workDate")} className={INPUT_CLASS} />
            {form.formState.errors.workDate && (
              <p className="mt-1 text-xs text-[#dc2626]">{form.formState.errors.workDate.message}</p>
            )}
          </div>

          {(watchType === "OVERTIME" || watchType === "HOLIDAY_OVERTIME") && (
            <div>
              <label className={LABEL_CLASS}>Overtime Hours</label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="12"
                {...form.register("overtimeHours", { valueAsNumber: true })}
                className={INPUT_CLASS}
                placeholder="e.g. 2.5"
              />
              {form.formState.errors.overtimeHours && (
                <p className="mt-1 text-xs text-[#dc2626]">{form.formState.errors.overtimeHours.message}</p>
              )}
            </div>
          )}

          <div>
            <label className={LABEL_CLASS}>Description</label>
            <textarea
              {...form.register("description")}
              rows={3}
              className={INPUT_CLASS + " resize-none"}
              placeholder="Briefly describe the work done…"
            />
            {form.formState.errors.description && (
              <p className="mt-1 text-xs text-[#dc2626]">{form.formState.errors.description.message}</p>
            )}
          </div>

          {submit.isError && (
            <div className="flex items-center gap-2 rounded-lg bg-[#fef2f2] border border-[#fecaca] px-4 py-3 text-sm text-[#dc2626]">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{(submit.error as Error)?.message ?? "Failed to submit"}</span>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-lg border border-[#e2e8f0] px-4 py-2.5 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submit.isPending}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#2447d9] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#1e3a8a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit Request
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
